import { Search, X, SlidersHorizontal, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { KbSourceType } from '@/hooks/useKnowledgeBase';
import { SELECTABLE_SOURCE_TYPES, sourceTypeMeta } from '../kbDisplay';

/**
 * Sort order for the KB entries list. `newest` and `oldest` sort by the
 * entry `created_at` timestamp; `question-az` and `question-za` sort the
 * question text alphabetically using `localeCompare`.
 */
export type KbSortOption = 'newest' | 'oldest' | 'question-az' | 'question-za';
/**
 * Staleness filter for the KB entries list. `all` applies no staleness
 * filter, `stale` keeps only entries with `is_stale = true`, and
 * `not-stale` keeps only entries with `is_stale = false`.
 */
export type StaleFilter = 'all' | 'stale' | 'not-stale';

/**
 * Props for {@link KbFilterBar}.
 *
 * The component is fully controlled — the parent owns every filter and
 * sort value and updates them through the matching `on*Change` callback.
 */
export interface KbFilterBarProps {
  /** Current free-text search query bound to the search input. */
  searchQuery: string;
  /** Called with the new search query whenever the search input changes or is cleared. */
  onSearchChange: (query: string) => void;
  /** Currently selected category, or `null` when no category filter is applied. */
  selectedCategory: string | null;
  /** Called with the new category, or `null` to clear the category filter. */
  onCategoryChange: (category: string | null) => void;
  /** Category options shown in the category dropdown. */
  categories: string[];
  /** Currently selected source type, or `null` when no source-type filter is applied. */
  selectedSourceType: KbSourceType | null;
  /** Called with the new source type, or `null` to clear the source-type filter. */
  onSourceTypeChange: (sourceType: KbSourceType | null) => void;
  /** Current staleness filter selection. */
  staleFilter: StaleFilter;
  /** Called with the new staleness filter value. */
  onStaleFilterChange: (filter: StaleFilter) => void;
  /** Current sort selection. */
  sortBy: KbSortOption;
  /** Called with the new sort value. */
  onSortChange: (sort: KbSortOption) => void;
  /** Number of KB entries matching the current filter set; displayed as the entry count. */
  resultCount: number;
  /** Optional class name appended to the root container. */
  className?: string;
}

const SORT_OPTIONS: Array<{ value: KbSortOption; label: string }> = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'question-az', label: 'Question A-Z' },
  { value: 'question-za', label: 'Question Z-A' },
];

const STALE_OPTIONS: Array<{ value: StaleFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'stale', label: 'Stale Only' },
  { value: 'not-stale', label: 'Not Stale' },
];

/**
 * Filter and sort controls for the KB admin entries view.
 *
 * Renders a search input, sort dropdown, result-count text, category
 * dropdown, source-type pill row, staleness pill row, an active-filter
 * badge strip, and a `Clear filters` button that appears whenever the
 * search query is non-empty, a category is selected, a source type is
 * selected, or the staleness filter is not `all`.
 */
export function KbFilterBar({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categories,
  selectedSourceType,
  onSourceTypeChange,
  staleFilter,
  onStaleFilterChange,
  sortBy,
  onSortChange,
  resultCount,
  className,
}: KbFilterBarProps) {
  const hasActiveFilters =
    searchQuery.length > 0 ||
    selectedCategory !== null ||
    selectedSourceType !== null ||
    staleFilter !== 'all';

  const handleClearFilters = () => {
    onSearchChange('');
    onCategoryChange(null);
    onSourceTypeChange(null);
    onStaleFilterChange('all');
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Search and Sort Row */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search questions or responses..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value as KbSortOption)}
            className="text-sm border rounded-md px-2 py-1.5 bg-background"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Result Count */}
        <div className="text-sm text-muted-foreground ml-auto">
          {resultCount.toLocaleString()} {resultCount === 1 ? 'entry' : 'entries'}
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <X className="h-4 w-4 mr-1" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Filter Row */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Category Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Category:</span>
          <select
            value={selectedCategory || ''}
            onChange={(e) => onCategoryChange(e.target.value || null)}
            className="text-sm border rounded-md px-2 py-1 bg-background min-w-[120px]"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div className="h-4 w-px bg-border" />

        {/* Source Type Filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground mr-1">Source:</span>
          <button
            type="button"
            onClick={() => onSourceTypeChange(null)}
            className={cn(
              'px-2 py-1 text-xs rounded-md transition-colors',
              selectedSourceType === null
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            All
          </button>
          {SELECTABLE_SOURCE_TYPES.map((source) => (
            <button
              key={source.value}
              type="button"
              onClick={() =>
                onSourceTypeChange(selectedSourceType === source.value ? null : source.value)
              }
              className={cn(
                'px-2 py-1 text-xs rounded-md transition-colors',
                selectedSourceType === source.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {source.label}
            </button>
          ))}
        </div>

        <div className="h-4 w-px bg-border" />

        {/* Stale Filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground mr-1">Status:</span>
          {STALE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onStaleFilterChange(option.value)}
              className={cn(
                'px-2 py-1 text-xs rounded-md transition-colors',
                staleFilter === option.value
                  ? option.value === 'stale'
                    ? 'bg-amber-500 text-white'
                    : 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          {searchQuery && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => onSearchChange('')}>
              Search: "{searchQuery}"
              <X className="h-3 w-3 ml-1" />
            </Badge>
          )}
          {selectedCategory && (
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => onCategoryChange(null)}
            >
              Category: {selectedCategory}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          )}
          {selectedSourceType && (
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => onSourceTypeChange(null)}
            >
              Source: {sourceTypeMeta(selectedSourceType).label}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          )}
          {staleFilter !== 'all' && (
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => onStaleFilterChange('all')}
            >
              {staleFilter === 'stale' ? 'Stale Only' : 'Not Stale'}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
